// La lista del costado de «Turbo · En vivo»: los Turbos del día en una de tres
// vistas, la misma para todos (la elige un Administrador Total en Turbo →
// Configuración). Las acciones están en el popup de la tienda: tocar un Turbo
// lleva el mapa a su tienda y lo abre ahí.
//   · linea: una línea por Turbo en tres grupos (para resolver, en curso y
//     cerrados, estos plegados).
//   · tienda: una fila por tienda con su alerta; se despliega para ver sus Turbos.
//   · tabla: vence, destinatario, tienda y estado, con orden por columna.

import React, { useState } from "react";
import { TURBO_ESTADO_COLOR, TURBO_ESTADO_LABEL, TurboEstado, TurboHoy } from "../../../lib/mapLayers";
import { driverName } from "../../../lib/driverName";
import { COLOR, domicilioDe, fmtDia, fmtHoraTurbo, fmtMin, fmtTime, fmtVence, SIN_MARCAR_AVISO, VistaLista } from "../../../lib/turboPopup";

// Lo que cambió en el último sondeo (nuevo o de estado), resaltado un rato.
export type Resaltados = Record<number, { hasta: number; nuevo: boolean }>;
type Props = {
  vista: VistaLista;
  // Ya filtrados por la tienda elegida.
  turbos: TurboHoy[];
  // Sin el filtro, si hoy hay alguno (para decir «nada con ese filtro»).
  hayTurbos: boolean;
  tiendasSinMarcar: Set<number>;
  resaltados: Resaltados;
  onTurbo: (t: TurboHoy) => void;
};

const ORDEN: TurboEstado[] = ["SIN_CHOFER", "PROGRAMADO", "CON_CHOFER", "EN_CAMINO", "NO_ENTREGADO", "ENTREGADO", "CANCELADO"];
// Menos que esto para el vencimiento: en amarillo.
const URGENTE_SEG = 30 * 60;

const muted: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const cabecera: React.CSSProperties = { ...muted, fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", padding: "12px 6px 4px" };
const filaBtn: React.CSSProperties = { width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "8px 6px", border: "none", borderRadius: "8px", font: "inherit", fontSize: "13px", color: "var(--text-primary)", textAlign: "left", cursor: "pointer", transition: "background 600ms ease" };
const plegarBtn: React.CSSProperties = { ...cabecera, display: "flex", alignItems: "center", gap: "6px", width: "100%", border: "none", background: "none", cursor: "pointer", textAlign: "left" };
const punto = (color: string): React.CSSProperties => ({ width: "8px", height: "8px", borderRadius: "50%", background: color, flex: "none" });
const corto: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const flecha = (abierta: boolean): React.CSSProperties => ({ display: "inline-block", transition: "transform 150ms", transform: abierta ? "rotate(90deg)" : "none" });

const nombreDe = (t: TurboHoy) => t.nombre_destinatario ?? (t.tracking != null ? String(t.tracking) : `#${t.delivery_id}`);
const fondoDe = (t: TurboHoy, r?: { nuevo: boolean }) =>
  r ? (r.nuevo ? "rgba(79,140,255,0.14)" : "rgba(63,185,80,0.12)") : t.estado === "SIN_CHOFER" && !t.terminal ? "rgba(248,81,73,0.06)" : "transparent";
const porUrgencia = (a: TurboHoy, b: TurboHoy) => ORDEN.indexOf(a.estado) - ORDEN.indexOf(b.estado) || (a.seconds_to_deadline ?? 1e9) - (b.seconds_to_deadline ?? 1e9);
const porCierre = (a: TurboHoy, b: TurboHoy) => new Date(b.estado_at ?? 0).getTime() - new Date(a.estado_at ?? 0).getTime();
const diaProgramado = (t: TurboHoy) => {
  const d = t.despacho?.scheduled_date;
  return d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : t.vence_at ? fmtDia(t.vence_at) : "otro día";
};

// Para resolver (sin chofer o programado), en curso o cerrado.
export const grupoDe = (t: TurboHoy): "resolver" | "curso" | "cerrados" =>
  t.terminal ? "cerrados" : t.estado === "SIN_CHOFER" || t.estado === "PROGRAMADO" ? "resolver" : "curso";

// Lo de la derecha de una fila, en corto (el grupo y el punto ya dicen el
// estado; el detalle va en el título y en el popup): cuánto falta, cuándo
// llega o cómo terminó. En rojo lo vencido o tarde; en amarillo, lo que vence
// en menos de media hora.
export function aLaDerecha(t: TurboHoy): { texto: string; color?: string } {
  const st = TURBO_ESTADO_LABEL[t.estado];
  if (t.terminal) return { texto: t.estado_at ? `${st} ${fmtHoraTurbo(t, t.estado_at)}` : st };
  if (t.estado === "PROGRAMADO") return { texto: `${st} · ${diaProgramado(t)}` };
  const h = t.plan?.holgura_min;
  if (t.plan?.eta) return h != null && h < 0 ? { texto: `${-h} min tarde`, color: COLOR.rojo } : { texto: `llega ~${fmtTime(t.plan.eta)}` };
  const s = t.seconds_to_deadline;
  if (s != null && t.vence_at) {
    if (s < 0) return { texto: "vencido", color: COLOR.rojo };
    return { texto: `faltan ${fmtMin(s)}`, color: s < URGENTE_SEG ? COLOR.amarillo : undefined };
  }
  return { texto: st, color: t.estado === "NO_ENTREGADO" ? COLOR.rojo : undefined };
}

// Todo junto, para el título de la fila (al pasar el mouse). La fila es
// angosta: el domicilio va solo acá (y entero en el popup).
export const tituloDe = (t: TurboHoy) => [
  nombreDe(t), domicilioDe(t), t.store_name ?? "tienda", TURBO_ESTADO_LABEL[t.estado],
  t.chofer ? driverName(t.chofer) : "",
  !t.terminal && t.vence_at ? `vence ${fmtVence(t, t.vence_at)}` : "",
  t.terminal && t.estado_at ? fmtHoraTurbo(t, t.estado_at) : "",
].filter(Boolean).join(" · ");

function MarcaSinTurbo() {
  return <span title={SIN_MARCAR_AVISO} aria-label={SIN_MARCAR_AVISO} style={{ color: COLOR.amarillo, fontWeight: 800, marginLeft: "4px" }}>!</span>;
}

// Una línea por Turbo: el punto del estado, el destinatario, la tienda (si va)
// y lo de la derecha.
function Fila({ t, conTienda, sinMarcar, resaltado, onTurbo }: { t: TurboHoy; conTienda: boolean; sinMarcar: boolean; resaltado?: { nuevo: boolean }; onTurbo: (t: TurboHoy) => void }) {
  const der = aLaDerecha(t);
  return (
    <button type="button" title={tituloDe(t)} onClick={() => onTurbo(t)} style={{ ...filaBtn, background: fondoDe(t, resaltado), opacity: t.terminal && !resaltado ? 0.7 : 1 }}>
      <span aria-hidden="true" style={punto(TURBO_ESTADO_COLOR[t.estado])} />
      <span style={{ ...corto, flex: 1, minWidth: 0 }}>
        {t.de_dias_anteriores && t.entro_at ? <span title={`Entró el ${fmtDia(t.entro_at)} a las ${fmtTime(t.entro_at)}`} style={{ ...muted, fontWeight: 700, color: COLOR.amarillo, marginRight: "6px" }}>{fmtDia(t.entro_at)}</span> : null}
        <b>{nombreDe(t)}</b>
        {conTienda ? <span style={muted}> {t.store_name ?? "tienda"}{sinMarcar ? <MarcaSinTurbo /> : null}</span> : null}
        {resaltado?.nuevo ? <span style={{ ...muted, color: COLOR.azul, fontWeight: 700 }}> NUEVO</span> : null}
      </span>
      <span style={{ ...muted, whiteSpace: "nowrap", color: der.color ?? muted.color, fontWeight: der.color ? 600 : 400 }}>{der.texto}</span>
    </button>
  );
}

function VistaLinea({ turbos, tiendasSinMarcar, resaltados, onTurbo }: Props) {
  const [verCerrados, setVerCerrados] = useState(false);
  const resolver = turbos.filter((t) => grupoDe(t) === "resolver").sort(porUrgencia);
  const curso = turbos.filter((t) => grupoDe(t) === "curso").sort(porUrgencia);
  const cerrados = turbos.filter((t) => grupoDe(t) === "cerrados").sort(porCierre);
  const fila = (t: TurboHoy) => <Fila key={t.delivery_id} t={t} conTienda sinMarcar={t.store_id != null && tiendasSinMarcar.has(t.store_id)} resaltado={resaltados[t.delivery_id]} onTurbo={onTurbo} />;
  return (
    <div>
      {resolver.length ? <><div style={cabecera}>Para resolver · {resolver.length}</div>{resolver.map(fila)}</> : null}
      {curso.length ? <><div style={cabecera}>En curso · {curso.length}</div>{curso.map(fila)}</> : null}
      {cerrados.length ? (
        <>
          <button type="button" style={plegarBtn} aria-expanded={verCerrados} onClick={() => setVerCerrados((v) => !v)}>
            Cerrados · {cerrados.length} <span aria-hidden="true" style={flecha(verCerrados)}>›</span>
          </button>
          {verCerrados ? cerrados.map(fila) : null}
        </>
      ) : null}
    </div>
  );
}

// Una tienda de la vista «por tienda»: sus Turbos y lo que hay que mirar.
export type TiendaDelDia = { id: number; nombre: string; turbos: TurboHoy[]; abiertos: number; sinChofer: number; vencidos: number; tarde: number; entregados: number; cancelados: number };
const vencido = (t: TurboHoy) => !t.terminal && t.estado !== "PROGRAMADO" && t.seconds_to_deadline != null && t.seconds_to_deadline < 0;
// Las que tienen algo vencido primero, después las de más sin chofer, las que
// llegan tarde y las de más abiertos.
export function tiendasDelDia(turbos: TurboHoy[]): TiendaDelDia[] {
  const m = new Map<number, TiendaDelDia>();
  for (const t of turbos) {
    const id = t.store_id ?? 0;
    const s = m.get(id) ?? { id, nombre: t.store_name ?? (t.store_id != null ? `Tienda ${t.store_id}` : "Sin tienda"), turbos: [], abiertos: 0, sinChofer: 0, vencidos: 0, tarde: 0, entregados: 0, cancelados: 0 };
    s.turbos.push(t);
    if (!t.terminal) s.abiertos++;
    if (!t.terminal && t.estado === "SIN_CHOFER") s.sinChofer++;
    if (vencido(t)) s.vencidos++;
    else if (!t.terminal && (t.plan?.holgura_min ?? 0) < 0) s.tarde++;
    if (t.estado === "ENTREGADO") s.entregados++;
    if (t.estado === "CANCELADO") s.cancelados++;
    m.set(id, s);
  }
  return [...m.values()].sort((a, b) => b.vencidos - a.vencidos || b.sinChofer - a.sinChofer || b.tarde - a.tarde || b.abiertos - a.abiertos || a.nombre.localeCompare(b.nombre, "es"));
}
export function alertaDe(s: TiendaDelDia): { texto: string; color: string } | null {
  if (s.vencidos) return { texto: s.vencidos === 1 ? "1 vencido" : `${s.vencidos} vencidos`, color: COLOR.rojo };
  if (s.sinChofer) return { texto: `${s.sinChofer} sin chofer`, color: COLOR.rojo };
  if (s.tarde) return { texto: s.tarde === 1 ? "1 llega tarde" : `${s.tarde} llegan tarde`, color: COLOR.amarillo };
  return null;
}
const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;
const resumenTienda = (s: TiendaDelDia) => [
  s.abiertos ? plural(s.abiertos, "abierto", "abiertos") : "nada abierto",
  s.entregados ? plural(s.entregados, "entregado", "entregados") : "",
  s.cancelados ? plural(s.cancelados, "cancelado", "cancelados") : "",
].filter(Boolean).join(" · ");

function VistaTienda({ turbos, tiendasSinMarcar, resaltados, onTurbo }: Props) {
  const tiendas = tiendasDelDia(turbos);
  // Sin tocar nada, abierta la primera (la más urgente).
  const [elegida, setElegida] = useState<number | null | undefined>(undefined);
  const abierta = elegida === undefined ? (tiendas[0]?.id ?? null) : elegida;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {tiendas.map((s) => {
        const open = s.id === abierta;
        const alerta = alertaDe(s);
        const propios = [...s.turbos.filter((t) => !t.terminal).sort(porUrgencia), ...s.turbos.filter((t) => t.terminal).sort(porCierre)];
        return (
          <div key={s.id} style={{ border: `1px solid ${alerta?.color === COLOR.rojo ? "rgba(248,81,73,0.4)" : "var(--border)"}`, borderRadius: "10px" }}>
            <button type="button" aria-expanded={open} onClick={() => setElegida(open ? null : s.id)}
              style={{ ...filaBtn, padding: "10px 12px", background: "transparent" }}>
              <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
                <b style={{ ...corto, fontSize: "14px" }}>{s.nombre}{tiendasSinMarcar.has(s.id) ? <MarcaSinTurbo /> : null}</b>
                <span style={muted}>{resumenTienda(s)}</span>
              </span>
              {alerta ? <span style={{ padding: "2px 9px", borderRadius: "999px", fontSize: "12px", fontWeight: 700, border: `1px solid ${alerta.color}`, color: alerta.color, whiteSpace: "nowrap" }}>{alerta.texto}</span> : null}
              <span aria-hidden="true" style={{ ...muted, ...flecha(open) }}>›</span>
            </button>
            {open ? (
              <div style={{ padding: "0 6px 6px" }}>
                {propios.map((t) => <Fila key={t.delivery_id} t={t} conTienda={false} sinMarcar={false} resaltado={resaltados[t.delivery_id]} onTurbo={onTurbo} />)}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

type Orden = "vence" | "tienda" | "estado";
// La columna del costado mide unos 300 px: el estado va como el punto (su
// nombre, en el título) para que entre el destinatario.
const COLUMNAS = "46px minmax(0, 1.8fr) minmax(0, 1fr) 18px";
const ORDENES: Record<Orden, (a: TurboHoy, b: TurboHoy) => number> = {
  vence: (a, b) => Number(a.terminal) - Number(b.terminal) || (a.seconds_to_deadline ?? 1e9) - (b.seconds_to_deadline ?? 1e9),
  tienda: (a, b) => (a.store_name ?? "").localeCompare(b.store_name ?? "", "es") || ORDENES.vence(a, b),
  estado: (a, b) => ORDEN.indexOf(a.estado) - ORDEN.indexOf(b.estado) || ORDENES.vence(a, b),
};
// La columna «Vence»: la hora; de un día anterior o un programado, solo el
// día (con la hora no entraba); nada en uno cerrado.
export function venceCorto(t: TurboHoy): { texto: string; color?: string } {
  if (t.terminal || !t.vence_at) return { texto: "—" };
  if (t.estado === "PROGRAMADO") return { texto: diaProgramado(t) };
  const s = t.seconds_to_deadline;
  return { texto: t.de_dias_anteriores ? fmtDia(t.vence_at) : fmtTime(t.vence_at), color: s != null && s < 0 ? COLOR.rojo : s != null && s < URGENTE_SEG ? COLOR.amarillo : undefined };
}

function VistaTabla({ turbos, tiendasSinMarcar, resaltados, onTurbo }: Props) {
  const [todos, setTodos] = useState(false);
  const [orden, setOrden] = useState<Orden>("vence");
  const filas = turbos.filter((t) => todos || !t.terminal || resaltados[t.delivery_id]).sort(ORDENES[orden]);
  const abiertos = turbos.filter((t) => !t.terminal).length;
  const segmento = (on: boolean): React.CSSProperties => ({ flex: 1, padding: "6px 8px", border: "none", borderRadius: "7px", font: "inherit", fontSize: "12px", fontWeight: 600, cursor: "pointer", background: on ? "var(--surface-alt)" : "transparent", color: on ? "var(--text-primary)" : "var(--text-secondary)" });
  const columna = (k: Orden, texto: string, nombre = texto) => (
    <button type="button" aria-pressed={orden === k} aria-label={`Ordenar por ${nombre.toLowerCase()}`} title={`Ordenar por ${nombre.toLowerCase()}`} onClick={() => setOrden(k)}
      style={{ ...cabecera, padding: 0, border: "none", background: "none", cursor: "pointer", textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", color: orden === k ? "var(--text-primary)" : muted.color }}>
      {texto}
    </button>
  );
  return (
    <div>
      <div style={{ display: "flex", gap: "4px", padding: "3px", borderRadius: "9px", border: "1px solid var(--border)", marginBottom: "8px" }}>
        <button type="button" aria-pressed={!todos} style={segmento(!todos)} onClick={() => setTodos(false)}>Abiertos · {abiertos}</button>
        <button type="button" aria-pressed={todos} style={segmento(todos)} onClick={() => setTodos(true)}>Todos · {turbos.length}</button>
      </div>
      {/* La columna por la que se ordena va resaltada (no entra una flecha). */}
      <div style={{ display: "grid", gridTemplateColumns: COLUMNAS, gap: "6px", padding: "0 6px 6px", borderBottom: "1px solid var(--border)" }}>
        {columna("vence", "Vence")}
        <span style={{ ...cabecera, padding: 0 }}>Destinatario</span>
        {columna("tienda", "Tienda")}
        {columna("estado", "●", "Estado")}
      </div>
      {!filas.length ? <div style={{ ...muted, padding: "10px 6px" }}>Ningún Turbo abierto ahora.</div> : null}
      {filas.map((t) => {
        const v = venceCorto(t);
        const r = resaltados[t.delivery_id];
        return (
          <button type="button" key={t.delivery_id} title={tituloDe(t)} onClick={() => onTurbo(t)}
            style={{ ...filaBtn, display: "grid", gridTemplateColumns: COLUMNAS, gap: "6px", minHeight: "32px", padding: "4px 6px", background: fondoDe(t, r), opacity: t.terminal && !r ? 0.65 : 1 }}>
            <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: v.color ?? "var(--text-primary)" }}>{v.texto}</span>
            <span style={{ ...corto, fontWeight: 600 }}>{nombreDe(t)}</span>
            <span style={{ ...corto, ...muted }}>{t.store_name ?? "—"}{t.store_id != null && tiendasSinMarcar.has(t.store_id) ? <MarcaSinTurbo /> : null}</span>
            <span role="img" aria-label={TURBO_ESTADO_LABEL[t.estado]} title={TURBO_ESTADO_LABEL[t.estado]} style={{ ...punto(TURBO_ESTADO_COLOR[t.estado]), width: "10px", height: "10px" }} />
          </button>
        );
      })}
    </div>
  );
}

export default function TurboVivoLista(props: Props) {
  if (!props.turbos.length) return <div style={muted}>{props.hayTurbos ? "Nada con ese filtro." : "Ningún Turbo hoy todavía."}</div>;
  if (props.vista === "tienda") return <VistaTienda {...props} />;
  if (props.vista === "tabla") return <VistaTabla {...props} />;
  return <VistaLinea {...props} />;
}
